<template>
  <div :style="fill ? 'position: relative; height: 100%;' : 'position: relative; min-height: 400px;'">
    <div v-if="isRendering" class="chart-loading">
      <i class="tim-icons icon-refresh-01 chart-spin"></i>
    </div>
    <canvas ref="barChart" :class="{ 'chart-hidden': isRendering, 'chart-canvas-fill': fill }"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js';
import currencies from "@/assets/currencies.json";
import {date} from "@/plugins/dates";
import _ from "lodash";

export default {
  name: 'BarChart',
  props: {
    // Fill the parent's height instead of the fixed 400px (dashboard tiles).
    fill: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Array,
      required: true
    },
    backgroundColors: {
      type: Array,
      default: () => ['#1d8cf8', '#e14eca', '#ff8d72', '#00f2c3', '#fd5d93',
        "#c5fb70", "#49341b", "#3aeae1", "#3012a1", "#f0bbb6", "#ff8d72", "#3d6066", "#32f432",
        "#680e6d", "#b84713", "#77e2b0", "#e56580", "#27ca36", "#109d60", "#42aa70", "#d562f0",
        "#dd7090", "#630a4e", "#3cd234", "#988ef5", "#14b0d4", "#e9557a", "#0a05ed", "#26c54d",
        "#dd4c7c", "#1d0b1c", "#d7aa41", "#431966", "#6642ae", "#67a026", "#858aa0", "#157a0c",
        "#fcef57", "#d75f5a", "#a44401", "#e4eb49", "#9bd97f", "#9c31e3", "#6902b0", "#e00e46",
        "#7d0f0b", "#db192a", "#868c17", "#e3512a", "#97dfa9", "#0387e6", "#6aae42", "#5f75d0",
        "#00f2c3", "#1c6a95", "#7b59fb", "#b54b6e", "#dbe2ad", "#056bbb", "#04c998", "#8ea315",
        "#708777", "#9bfcc2", "#fd5d93", "#0767da", "#ba2be0", "#a24bd0", "#4fc623", "#bce910",
        "#794dde", "#92f6a8", "#d773ee", "#e14eca", "#eb83b9", "#d69f69", "#5f7df0", "#225260",
        "#33da07", "#0e3fb8", "#25ed46", "#bbf5b4", "#1d8cf8", "#576fe8", "#ba3fec", "#3c8e4a",
        "#efe241", "#5a72dd", "#e29981", "#fbe33f", "#901a22", "#0b8752", "#a1e417", "#28bbe9",
        "#0fefae", "#b58bf8", "#7bf97a", "#834fb0", "#511c10", "#b930fa", "#1abf7e", "#c4e9a0",
        "#af409d", "#7c5913", "#7939cb", "#3e0d19"],
    },
    barBorderColors: {
      type: Array,
      default: () => ['#1d8cf8', '#e14eca', '#ff8d72', '#00f2c3', '#fd5d93',
        "#c5fb70", "#49341b", "#3aeae1", "#3012a1", "#f0bbb6", "#ff8d72", "#3d6066", "#32f432",
        "#680e6d", "#b84713", "#77e2b0", "#e56580", "#27ca36", "#109d60", "#42aa70", "#d562f0",
        "#dd7090", "#630a4e", "#3cd234", "#988ef5", "#14b0d4", "#e9557a", "#0a05ed", "#26c54d",
        "#dd4c7c", "#1d0b1c", "#d7aa41", "#431966", "#6642ae", "#67a026", "#858aa0", "#157a0c",
        "#fcef57", "#d75f5a", "#a44401", "#e4eb49", "#9bd97f", "#9c31e3", "#6902b0", "#e00e46",
        "#7d0f0b", "#db192a", "#868c17", "#e3512a", "#97dfa9", "#0387e6", "#6aae42", "#5f75d0",
        "#00f2c3", "#1c6a95", "#7b59fb", "#b54b6e", "#dbe2ad", "#056bbb", "#04c998", "#8ea315",
        "#708777", "#9bfcc2", "#fd5d93", "#0767da", "#ba2be0", "#a24bd0", "#4fc623", "#bce910",
        "#794dde", "#92f6a8", "#d773ee", "#e14eca", "#eb83b9", "#d69f69", "#5f7df0", "#225260",
        "#33da07", "#0e3fb8", "#25ed46", "#bbf5b4", "#1d8cf8", "#576fe8", "#ba3fec", "#3c8e4a",
        "#efe241", "#5a72dd", "#e29981", "#fbe33f", "#901a22", "#0b8752", "#a1e417", "#28bbe9",
        "#0fefae", "#b58bf8", "#7bf97a", "#834fb0", "#511c10", "#b930fa", "#1abf7e", "#c4e9a0",
        "#af409d", "#7c5913", "#7939cb", "#3e0d19"],
    },
    legendTextColor: {
      type: String,
      default: '#ffffff'
    },
    dataFormat: {
      type: String,
      default: 'count'
    },
    comparisonSeries: {
      type: Array,
      default: () => [],
      // each entry: { label: String, data: Array of {label, value} }
    },
    primaryLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      chart: null,
      isRendering: true,
      renderQueued: false,
      lastRenderSig: null,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currencies: currencies.currencies
    };
  },
  computed: {
    labels() {
      return this.chartData.map(item => item.label);
    },
    values() {
      return this.chartData.map(item => Number(item.value));
    },
    gridLineColor() {
      return localStorage.getItem('mode') !== '1' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    },
    axisTextColor() {
      return localStorage.getItem('mode') === '0' ? '#000000' : '#ffffff'
    }
  },
  mounted() {
    const currencyData = _.find(this.currencies, { code: this.currency });
    this.currency_symbol = currencyData ? currencyData.symbol : '$';
    this.scheduleCreateChart();
    if (this.fill) {
      window.addEventListener('dashboard-widget-resize', this.onTileResize);
    }
  },
  methods: {
    onTileResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    // Same stabilization as LineChart: coalesce renders to one per tick and
    // skip content-identical re-fires (the parent passes comparison-series via
    // a method call, so every parent flush hands us a new identical array).
    scheduleCreateChart() {
      if (this.renderQueued) return;
      this.renderQueued = true;
      this.$nextTick(() => {
        this.renderQueued = false;
        const sig = JSON.stringify([this.chartData, this.comparisonSeries, this.primaryLabel]);
        if (this.chart && sig === this.lastRenderSig) return;
        this.isRendering = true;
        this.createChart();
        this.lastRenderSig = sig;
        this.isRendering = false;
      });
    },
    formatCurrency(amount) {
      if (amount == null) return '';

      const value = parseFloat(amount);

      // Handle SEK and DKK formatting (postfix krone symbols)
      if (this.currency === 'SEK' || this.currency === 'DKK') {
        const formattedAmount = value.toLocaleString(this.currency === 'DKK' ? 'da-DK' : 'sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `${formattedAmount} ${this.currency === 'DKK' ? 'kr.' : 'kr'}`;
      }

      // Handle other currency formatting based on existing logic
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, {
          minimumFractionDigits: 2
        }).replace('.', ',');

        if (this.currency_format === 0) {
          return this.currency_symbol + localizedAmount;
        } else {
          return localizedAmount + ' ' + this.currency;
        }
      }

      // Default formatting
      if (this.currency_format === 0) {
        return this.currency_symbol + value.toFixed(2);
      } else {
        return value.toFixed(2) + ' ' + this.currency;
      }
    },

    getValueFormat(data_format, integer) {
      if (integer == null) {
        return '';
      }

      if (data_format !== 'count' && data_format !== 'quantity' && data_format !== 'date') {
        return this.formatCurrency(integer);
      }

      if (data_format === 'date') {
        return date(integer, this.date_format);
      }

      return Number(integer);
    },

    createChart() {
      if (this.chart) {
        this.chart.destroy();
      }
      const canvas = this.$refs.barChart;
      if (!this.fill) { canvas.height = 400; }

      // Without comparison: one dataset, one palette color per bar (existing
      // behavior). With comparison: grouped bars, color means PERIOD, and the
      // category axis is the union across periods (a category absent in a
      // period plots as 0). Primary keeps its returned order; categories seen
      // only in comparison periods append after.
      let chartLabels, datasets;
      if (this.comparisonSeries.length > 0) {
        const unionLabels = this.chartData.map(item => item.label);
        const seen = new Set(unionLabels);
        for (const series of this.comparisonSeries) {
          for (const item of (series.data || [])) {
            if (!seen.has(item.label)) {
              seen.add(item.label);
              unionLabels.push(item.label);
            }
          }
        }
        const periodColors = ['#1d8cf8', '#e14eca', '#ff8d72', '#00f2c3', '#fd5d93', '#c5fb70', '#3aeae1', '#988ef5', '#d7aa41', '#5f75d0'];
        const toValues = (rows) => {
          const byLabel = new Map((rows || []).map(item => [item.label, Number(item.value)]));
          return unionLabels.map(l => byLabel.has(l) ? byLabel.get(l) : 0);
        };
        chartLabels = unionLabels;
        datasets = [
          {
            label: this.primaryLabel || '',
            data: toValues(this.chartData),
            backgroundColor: periodColors[0],
            borderColor: periodColors[0],
            borderWidth: 1
          },
          ...this.comparisonSeries.map((series, idx) => ({
            label: series.label || '',
            data: toValues(series.data),
            backgroundColor: periodColors[(idx + 1) % periodColors.length],
            borderColor: periodColors[(idx + 1) % periodColors.length],
            borderWidth: 1
          }))
        ];
      } else {
        chartLabels = this.labels;
        datasets = [
          {
            label: '',
            data: this.values,
            backgroundColor: this.backgroundColors.slice(0, this.labels.length),
            borderColor: this.barBorderColors.slice(0, this.labels.length),
            borderWidth: 1
          }
        ];
      }

      const ctx = this.$refs.barChart.getContext('2d');
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: chartLabels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  fontColor: this.axisTextColor,
                  callback: value => this.getValueFormat(this.dataFormat, value),
                  stepSize: 10
                },
                gridLines: {
                  color: this.gridLineColor
                }
              }
            ],
            xAxes: [
              {
                ticks: {
                  fontColor: this.axisTextColor
                },
                gridLines: {
                  color: this.gridLineColor
                }
              }
            ]
          },
          legend: {
            display: this.comparisonSeries.length > 0,
            labels: { fontColor: this.axisTextColor, usePointStyle: false }
          },
          layout: {
            padding: {
              top: 20 // Adjusts spacing between chart and legend
            }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const value = dataset.data[tooltipItem.index];
                const label = data.labels[tooltipItem.index];
                const prefix = dataset.label ? `${dataset.label} - ` : '';
                return `${prefix}${label}: ${this.getValueFormat(this.dataFormat, value)}`;
              }
            }
          }
        }
      });
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.scheduleCreateChart();
      }
    },
    comparisonSeries: {
      deep: true,
      handler() {
        this.scheduleCreateChart();
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('dashboard-widget-resize', this.onTileResize);
    if (this.chart) {
      this.chart.destroy();
    }
  }
};
</script>

<style scoped>
canvas {
  height: 400px;
  display: block;
  margin-bottom: 40px; /* Adds space between chart and legend */
  padding-bottom: 10px; /* Adds space between chart and legend */
  transition: opacity 0.35s ease;
}
canvas.chart-canvas-fill {
  height: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
}
.chart-hidden {
  opacity: 0;
}
.chart-loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chart-spin {
  font-size: 1.5rem;
  color: rgba(125, 125, 125, 0.8);
  animation: chart-spin 1s linear infinite;
}
@keyframes chart-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
